// src/components/Pimgy/IndividualTransactionHistory.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import '../AdminDashboard/ExpenseManager.css';

const IndividualTransactionHistory = () => {
  const [searchValue, setSearchValue] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [membersSummary, setMembersSummary] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showMembersList, setShowMembersList] = useState(true);

  // Fetch all members summary when component loads
  useEffect(() => {
    const fetchAllMembers = async () => {
      try {
        setLoadingMembers(true);
        const response = await axios.get(`${API_BASE_URL}/api/pimgy/all-members-summary`);
        if (response.data && Array.isArray(response.data)) {
          setMembersSummary(response.data);
        } else {
          setMembersSummary([]);
        }
      } catch (error) {
        console.error('Error fetching members summary:', error);
        setMembersSummary([]);
        // Don't show alert on initial load, just log the error
        if (error.response?.status !== 404) {
          console.warn('Failed to load members summary:', error.response?.data?.error || error.message);
        }
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchAllMembers();
  }, []);

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      alert('Please enter a registration number or name to search');
      return;
    }

    try {
      setLoading(true);
      setSearched(true);
      setShowMembersList(false);
      const response = await axios.get(`${API_BASE_URL}/api/pimgy/individual-history`, {
        params: { search_value: searchValue.trim() }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching individual history:', error);
      alert(error.response?.data?.error || 'Failed to load transaction history');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    setTransactions([]);
    setSearched(false);
    setShowMembersList(true);
  };

  const printTransactionHistory = () => {
    if (transactions.length === 0) {
      alert('No transactions to print.');
      return;
    }

    const totalReceived = transactions.reduce((sum, t) => sum + parseFloat(t.received || 0), 0);
    const totalWithdrawn = transactions.reduce((sum, t) => sum + parseFloat(t.withdrawn || 0), 0);
    const finalBalance = transactions.length > 0 ? parseFloat(transactions[0].balance || 0) : 0;
    const finalInterest = transactions.length > 0 ? parseFloat(transactions[0].cumulative_interest || 0) : 0;
    const finalTotal = transactions.length > 0 ? parseFloat(transactions[0].total_with_interest || 0) : 0;
    const searchInfo = transactions.length > 0 
      ? `Registration No/Name: ${transactions[0].registration_no} - ${transactions[0].name}`
      : '';

    const historyContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
  .header { text-align: center; font-weight: bold; font-size: 1.5em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 1em; margin-bottom: 15px; }
  .search-info { text-align: center; margin-bottom: 20px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { border: 1px solid #000; padding: 8px; text-align: left; }
  th { background-color: #f0f0f0; font-weight: bold; }
  .right { text-align: right; }
  .total-row { background-color: #f0f0f0; font-weight: bold; }
  .footer { text-align: center; margin-top: 30px; font-size: 0.9em; }
</style>

<div class="header">M/S SHREE SAI FINANCE AND LEASING (R) & Leasing</div>
<div class="sub-header">Rudrawadi</div>
<div class="search-info">Pigmy Collection - Individual Transaction History</div>
<div class="search-info">${searchInfo}</div>

<table>
  <thead>
    <tr>
      <th>Date</th>
      <th>Registration No</th>
      <th>Name</th>
      <th>Finance Name</th>
      <th class="right">Received (₹)</th>
      <th class="right">Withdrawn (₹)</th>
      <th class="right">Balance (₹)</th>
      <th class="right">Interest @ 14% p.a. (₹)</th>
      <th class="right">Total + Interest (₹)</th>
      <th>Payment Mode</th>
      <th>Transaction ID</th>
    </tr>
  </thead>
  <tbody>
    ${transactions.map(t => `
      <tr>
        <td>${formatDateToDDMMYYYY(t.transaction_date)}</td>
        <td>${t.registration_no}</td>
        <td>${t.name}</td>
        <td>${t.finance_name || '-'}</td>
        <td class="right">${Number(t.received || 0).toFixed(2)}</td>
        <td class="right">${Number(t.withdrawn || 0).toFixed(2)}</td>
        <td class="right">${Number(t.balance || 0).toFixed(2)}</td>
        <td class="right">${Number(t.cumulative_interest || 0).toFixed(2)}</td>
        <td class="right">${Number(t.total_with_interest || 0).toFixed(2)}</td>
        <td>${t.payment_mode || '-'}</td>
        <td>${t.transaction_id || '-'}</td>
      </tr>
    `).join('')}
    <tr class="total-row">
      <td colspan="6" style="text-align: right;"><strong>TOTAL:</strong></td>
      <td class="right"><strong>₹ ${finalBalance.toFixed(2)}</strong></td>
      <td class="right"><strong>₹ ${finalInterest.toFixed(2)}</strong></td>
      <td class="right"><strong>₹ ${finalTotal.toFixed(2)}</strong></td>
      <td colspan="2"></td>
    </tr>
  </tbody>
</table>

<div class="footer">
  Printed on: ${formatDateToDDMMYYYY(new Date().toISOString().split('T')[0])}
</div>
`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(historyContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="transaction-history-section">
      <h3>Individual Transaction History</h3>
      
      <div className="filters-container">
        <div className="filter-group" style={{ flex: 1 }}>
          <label>Search by Registration No or Name:</label>
          <input
            type="text"
            className="filter-input"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="Enter registration number or name"
          />
        </div>
        <div className="filter-buttons">
          <button 
            className="clear-button" 
            onClick={clearSearch}
            style={{ marginRight: '10px' }}
          >
            Clear
          </button>
          <button 
            className="print-button" 
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
          {transactions.length > 0 && (
            <button 
              className="print-button" 
              onClick={printTransactionHistory}
              style={{ marginLeft: '10px' }}
            >
              Print
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading transactions...</div>
      ) : searched && transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No transactions found for the search criteria.
        </div>
      ) : transactions.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Registration No</th>
                <th>Name</th>
                <th>Finance Name</th>
                <th>Received (₹)</th>
                <th>Withdrawn (₹)</th>
                <th style={{ textAlign: 'right' }}>Balance (₹)</th>
                <th style={{ textAlign: 'right' }}>Interest @ 14% p.a. (₹)</th>
                <th style={{ textAlign: 'right' }}>Total + Interest (₹)</th>
                <th>Payment Mode</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id}>
                  <td>{formatDateToDDMMYYYY(t.transaction_date)}</td>
                  <td>{t.registration_no}</td>
                  <td>{t.name}</td>
                  <td>{t.finance_name || '-'}</td>
                  <td>₹{Number(t.received || 0).toFixed(2)}</td>
                  <td>₹{Number(t.withdrawn || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>₹{Number(t.balance || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>₹{Number(t.cumulative_interest || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>₹{Number(t.total_with_interest || 0).toFixed(2)}</td>
                  <td>{t.payment_mode || '-'}</td>
                  <td>{t.transaction_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : showMembersList && !searched ? (
        <div style={{ overflowX: 'auto', marginTop: '20px' }}>
          {loadingMembers ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading members...</div>
          ) : membersSummary.length > 0 ? (
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Serial No</th>
                  <th>Registration No</th>
                  <th>Name</th>
                  <th>Finance Name</th>
                  <th style={{ textAlign: 'right' }}>Total Amount Paid (₹)</th>
                  <th style={{ textAlign: 'right' }}>Interest @ 14% p.a. (₹)</th>
                  <th style={{ textAlign: 'right' }}>Total Amount + Interest (₹)</th>
                </tr>
              </thead>
              <tbody>
                {membersSummary.map((member, index) => (
                  <tr key={member.registration_no || index}>
                    <td>{index + 1}</td>
                    <td>{member.registration_no || '-'}</td>
                    <td>{member.name || '-'}</td>
                    <td>{member.finance_name || '-'}</td>
                    <td style={{ textAlign: 'right' }}>₹{Number(member.total_paid || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right' }}>₹{Number(member.interest || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right' }}>₹{Number(member.total_with_interest || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                  <td colSpan="4" style={{ textAlign: 'right' }}>TOTAL:</td>
                  <td style={{ textAlign: 'right' }}>
                    ₹{membersSummary.reduce((sum, m) => sum + Number(m.total_paid || 0), 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    ₹{membersSummary.reduce((sum, m) => sum + Number(m.interest || 0), 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    ₹{membersSummary.reduce((sum, m) => sum + Number(m.total_with_interest || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No members found.
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Enter a registration number or name to search for transaction history.
        </div>
      )}
    </div>
  );
};

export default IndividualTransactionHistory;

